<template>
    <div>
        <!-- Label -->
        <label v-if="label" class="ppv2-flex ppv2-items-center ppv2-text-sm poppinsMedium mb-2">
            <component v-if="leftIcon" :is="leftIcon" class="ppv2-w-4 ppv2-h-4 ppv2-mr-2 ppv2-text-gray-700" />
            {{ $t(label) }}<span v-if="required" class="text-purple">*</span>
        </label>

        <!-- Description -->
        <p v-if="description" class="ppv2-text-xs ppv2-text-gray-500 ppv2-mb-2 mb-2">
            {{ $t(description) }}
        </p>

        <!-- Upload Area -->
        <div :class="uploadAreaClasses" @click="triggerFileInput" @dragover.prevent @dragleave.prevent @drop.prevent="handleDrop">
            <input
                ref="fileInput"
                type="file"
                :accept="accept"
                :multiple="multiple"
                class="ppv2-hidden"
                @change="handleFileSelect"
            />

            <div
                v-if="!previewUrl"
                class="ppv2-text-center"
                :style="{ paddingTop: normalizeSizeVertical(32, true), paddingBottom: normalizeSizeVertical(32, true) }"
            >
                <svg width="32" height="33" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"
                        fill="currentColor"
                    />
                </svg>
                <div class="ppv2-mt-4">
                    <p class="ppv2-text-sm poppinsMedium">{{ $t(uploadText) }}</p>
                    <p class="ppv2-text-xs ppv2-text-gray-500 ppv2-mt-1">{{ $t(formatText) }}</p>
                </div>
            </div>

            <div v-if="previewUrl" class="ppv2-relative">
                <img
                    :src="previewUrl"
                    :alt="fileName"
                    :class="[
                        'ppv2-w-full ppv2-h-48 ppv2-object-cover',
                        isCircleSelected ? 'ppv2-rounded-full' : 'ppv2-rounded-md',
                    ]"
                />
                <div
                    :class="[
                        'ppv2-absolute ppv2-inset-0 ppv2-bg-black ppv2-bg-opacity-50 ppv2-flex ppv2-items-center ppv2-justify-center',
                        isCircleSelected ? 'ppv2-rounded-full' : 'ppv2-rounded-md',
                    ]"
                >
                    <div class="ppv2-flex ppv2-space-x-2">
                        <button
                            type="button"
                            @click.stop="changeFile"
                            class="ppv2-bg-white ppv2-text-gray-700 ppv2-px-3 ppv2-py-1 ppv2-rounded hover:ppv2-bg-gray-100"
                        >
                            {{ $t('common.change') }}
                        </button>
                        <button
                            v-if="enableCropper"
                            type="button"
                            @click.stop="openCropper"
                            class="ppv2-bg-yellow-400 ppv2-text-white ppv2-px-3 ppv2-py-1 ppv2-rounded hover:ppv2-bg-yellow-500"
                        >
                            {{ $t('common.show') }}
                        </button>
                        <button
                            type="button"
                            @click.stop="removeFile"
                            class="ppv2-bg-red-500 ppv2-text-white ppv2-px-3 ppv2-py-1 ppv2-rounded hover:ppv2-bg-red-600"
                        >
                            {{ $t('common.delete') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!previewUrl && buttonText" class="ppv2-text-center">
            <button
                type="button"
                class="ppv2-mt-4 ppv2-w-full bg-purple ppv2-text-white ppv2-px-4 ppv2-py-2 ppv2-rounded-md"
                @click="triggerFileInput"
            >
                {{ $t(buttonText) }}
            </button>
        </div>

        <p v-if="helpText" class="ppv2-text-xs ppv2-text-gray-500">{{ $t(helpText) }}</p>
        <p v-if="error" class="ppv2-text-xs ppv2-text-red-600">{{ $t(error) }}</p>

        <!-- Cropper Modal -->
        <div
            v-if="showCropper && enableCropper"
            class="ppv2-fixed ppv2-inset-0 ppv2-z-50 ppv2-bg-black/60 ppv2-flex ppv2-items-center ppv2-justify-center"
        >
            <div
                class="ppv2-bg-white ppv2-rounded-xl ppv2-shadow-xl ppv2-w-full ppv2-p-6"
                :class="[isRemoteImage ? 'ppv2-max-w-md' : 'ppv2-max-w-5xl']"
            >
                <div v-if="!isRemoteImage" class="ppv2-flex ppv2-gap-2 ppv2-flex-wrap">
                    <button
                        type="button"
                        v-for="option in aspectOptions"
                        :key="option.label"
                        @click="selectAspectRatio(option)"
                        :class="[
                            selectedAspectRatio === option.value && isCircleSelected === !!option.circle
                                ? 'bg-purple ppv2-text-white'
                                : 'ppv2-bg-gray-200 ppv2-text-gray-800',
                            'ppv2-px-3 ppv2-py-1 ppv2-text-sm ppv2-rounded-md',
                        ]"
                    >
                        {{ $t(option.label) }}
                    </button>
                </div>

                <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-mt-4" :style="{ gap: normalizeSize(16, true) }">
                    <div
                        v-if="isRemoteImage"
                        class="ppv2-col-span-2 ppv2-border ppv2-border-gray-200 ppv2-rounded ppv2-p-2 ppv2-bg-gray-50"
                    >
                        <h3 class="ppv2-text-sm poppinsMedium ppv2-mb-2">Aperçu</h3>
                        <img :src="previewUrl" class="ppv2-w-full ppv2-h-[250px] ppv2-object-contain ppv2-rounded" />
                    </div>
                    <template v-else>
                        <cropper
                            ref="cropper"
                            :src="imageUrl"
                            class="ppv2-w-full ppv2-h-[400px] ppv2-border ppv2-border-gray-200 ppv2-rounded-md"
                            :stencil-component="isCircleSelected ? 'circle-stencil' : undefined"
                            :stencil-props="{ aspectRatio: selectedAspectRatio }"
                            :transformations.sync="transformations"
                            :wheel-resize-mode="'zoom'"
                            :auto-zoom="true"
                            @change="onCropChange"
                        />
                        <div class="ppv2-border ppv2-border-gray-200 ppv2-rounded-md ppv2-p-2 ppv2-bg-gray-50">
                            <h3 class="ppv2-text-sm poppinsMedium ppv2-mb-2">Aperçu</h3>
                            <img :src="previewUrl" class="ppv2-w-full ppv2-h-[250px] ppv2-object-contain ppv2-rounded" />
                        </div>
                    </template>
                </div>

                <div class="ppv2-flex ppv2-justify-end ppv2-gap-2 ppv2-mt-4">
                    <button
                        type="button"
                        @click="showCropper = false"
                        class="ppv2-bg-white ppv2-border ppv2-border-gray-300 ppv2-text-gray-700 ppv2-px-4 ppv2-py-2 ppv2-rounded-md hover:ppv2-bg-gray-100"
                    >
                        {{ $t('common.cancel') }}
                    </button>
                    <button
                        v-if="!isRemoteImage"
                        type="button"
                        @click="confirmCrop"
                        class="bg-purple ppv2-text-white ppv2-px-4 ppv2-py-2 ppv2-rounded-md hover:ppv2-bg-primary-700"
                    >
                        {{ $t('common.cropConfirm') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSizeVertical from '../../utils/normalizeSizeVertical'

import { Cropper, CircleStencil } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

export default {
    name: 'BaseFileUpload',
    components: { Cropper, CircleStencil },
    props: {
        value: [File, String],
        label: String,
        description: String,
        accept: { type: String, default: 'image/*' },
        multiple: Boolean,
        required: Boolean,
        uploadText: String,
        formatText: String,
        buttonText: String,
        helpText: String,
        error: String,
        enableCropper: { type: Boolean, default: true },
        leftIcon: String,
    },
    data() {
        return {
            previewUrl: null,
            fileName: '',
            showCropper: false,
            imageUrl: '',
            transformations: {},
            selectedAspectRatio: 1,
            isCircleSelected: false,
            aspectOptions: [
                { label: 'format.square', value: 1 },
                { label: 'format.free', value: null },
                { label: 'format.portrait', value: 3 / 4 },
                { label: 'format.landscape', value: 4 / 3 },
                { label: 'format.facebookCover', value: 820 / 312 },
            ],
        }
    },
    computed: {
        uploadAreaClasses() {
            return [
                'ppv2-border',
                'ppv2-border-primary-400',
                'ppv2-border-dashed',
                'ppv2-rounded-lg',
                'ppv2-cursor-pointer',
                'ppv2-transition-colors',
                'ppv2-bg-[#7D2DBD26]',
                this.error ? 'ppv2-border-red-300' : 'hover:ppv2-border-primary-500',
            ]
        },
        isRemoteImage() {
            return typeof this.value === 'string' && this.value.startsWith('http')
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                if (typeof newVal === 'string' && newVal.startsWith('http')) {
                    this.previewUrl = newVal
                    this.imageUrl = newVal
                    this.fileName = 'remote-image.png'
                } else if (newVal instanceof File) {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        this.previewUrl = e.target.result
                        this.imageUrl = e.target.result
                        this.fileName = newVal.name
                    }
                    reader.readAsDataURL(newVal)
                } else {
                    this.previewUrl = null
                    this.imageUrl = null
                    this.fileName = ''
                }
            },
        },
    },
    methods: {
        normalizeSizeVertical,
        triggerFileInput() {
            this.$refs.fileInput.click()
        },
        handleFileSelect(e) {
            const file = e.target.files[0]
            if (file) this.processFile(file)
        },
        handleDrop(e) {
            const files = e.dataTransfer.files
            if (files.length > 0) this.processFile(files[0])
        },
        changeFile() {
            this.previewUrl = null
            this.imageUrl = null
            this.fileName = ''
            this.showCropper = false
            this.$emit('input', null)
            this.$refs.fileInput.value = ''
            this.triggerFileInput()
        },
        removeFile() {
            this.previewUrl = null
            this.imageUrl = null
            this.fileName = ''
            this.showCropper = false
            this.$emit('input', null)
            this.$refs.fileInput.value = ''
        },
        processFile(file) {
            this.fileName = file.name
            this.$emit('input', file)
            const reader = new FileReader()
            reader.onload = (e) => {
                this.imageUrl = e.target.result
                if (this.enableCropper) {
                    this.previewUrl = null
                    this.showCropper = true
                } else {
                    this.previewUrl = e.target.result
                }
            }
            reader.readAsDataURL(file)
            this.$refs.fileInput.value = ''
        },

        onCropChange({ canvas }) {
            if (canvas) {
                this.previewUrl = canvas.toDataURL()
            }
        },
        openCropper() {
            if (this.previewUrl) {
                this.imageUrl = this.previewUrl
                this.showCropper = true
            }
        },
        confirmCrop() {
            const canvas = this.$refs.cropper.getResult().canvas
            canvas.toBlob((blob) => {
                const file = new File([blob], this.fileName, { type: 'image/webp' })
                this.previewUrl = URL.createObjectURL(file)
                this.$emit('input', file)
                this.showCropper = false
            }, 'image/webp')
        },
        selectAspectRatio(option) {
            this.selectedAspectRatio = option.value
            this.isCircleSelected = !!option.circle
        },
    },
}
</script>
